import React from 'react';
import { Card } from '@components/atoms/Card';
import { Progress } from '@components/atoms/Progress';
import { Button } from '@components/atoms/Button';
import { HealthPill } from '@components/molecules/HealthPill';
import { ClientCounter } from '@pages/dashboard/client/ClientCounter';
import {
  ClientCounterKind,
  completionPercent,
  formatRelativeActivity,
  healthBarVariant,
  nonZeroCounters,
} from '@pages/dashboard/client/clientProgressFormat';
import { fillTemplate } from '@pages/dashboard/client/clientProjectDisplay';
import { ClientProjectOverview } from '@models/client.model';
import { SubcontractorHealth } from '@models/subcontractor.model';
import { useTranslation } from '@hooks/useTranslation';
import { ClientNextUp } from './ClientNextUp';
import { ClientTabId } from '../../clientTabs';
import { ClientTaskGroupId } from '../ClientTasksTab/clientTaskGroups';

interface ClientProgressHeaderProps {
  overview?: ClientProjectOverview;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  /** Tabs this viewer has; a counter is a button only when its destination tab exists. */
  visibleTabs: ClientTabId[];
  /**
   * Activating a counter selects its tab and, for the Tasks tab, names the group to scroll to
   * (UX story-226 "Interaction Patterns": Overdue / Due this week → In progress, Awaiting your
   * approval → that group). The tab may already be open, so the group is what makes the click
   * visibly do something.
   */
  onSelectTab?: (tab: ClientTabId, group?: ClientTaskGroupId) => void;
}

const DESTINATION: Record<
  ClientCounterKind,
  { tab: ClientTabId; group?: ClientTaskGroupId }
> = {
  overdue: { tab: 'tasks', group: 'inProgress' },
  dueThisWeek: { tab: 'tasks', group: 'inProgress' },
  awaitingApproval: { tab: 'tasks', group: 'awaiting' },
  openIncidents: { tab: 'incidents' },
};

/**
 * Sprint 44, story-226. The progress header above the tab strip for CLIENT and REVIEWER. It is
 * the always-visible tier: it renders regardless of every checklist key. Every number is a field
 * of the server's overview row; this component formats, it never counts. Money, assignee names
 * and write controls never appear here.
 */
export const ClientProgressHeader: React.FC<ClientProgressHeaderProps> = ({
  overview,
  isLoading = false,
  isError = false,
  onRetry,
  visibleTabs,
  onSelectTab,
}) => {
  const t = useTranslation('clientProgress');

  if (isLoading) {
    return (
      <Card
        data-testid="client-progress-header-skeleton"
        className="flex-shrink-0 animate-pulse">
        <div className="h-5 w-24 rounded-full bg-gray-200 dark:bg-gray-600" />
        <div className="mt-4 h-3 w-full rounded-full bg-gray-200 dark:bg-gray-600" />
        <div className="mt-4 flex gap-2">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-14 w-28 rounded-md bg-gray-200 dark:bg-gray-600"
            />
          ))}
        </div>
      </Card>
    );
  }

  if (isError || !overview) {
    return (
      <div
        data-testid="client-progress-header-error"
        className="flex-shrink-0 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
        <span>{t.unavailable}</span>
        {onRetry && (
          <Button variant="tertiary" onClick={onRetry}>
            {t.retry}
          </Button>
        )}
      </div>
    );
  }

  const percent = completionPercent(overview.completion);
  const isNone =
    overview.health === SubcontractorHealth.NONE ||
    overview.countableCount === 0;
  const isComplete = overview.health === SubcontractorHealth.COMPLETE;
  const counters = nonZeroCounters(overview);
  const lastActivity = overview.lastActivityAt
    ? formatRelativeActivity(
        overview.lastActivityAt,
        new Date(),
        {
          today: t.lastActivityToday,
          yesterday: t.lastActivityYesterday,
          daysAgo: t.lastActivityDaysAgo,
        },
        (date) => date.toLocaleDateString('en-US'),
      )
    : '';

  return (
    <Card data-testid="client-progress-header" className="flex-shrink-0">
      <div className="flex items-center justify-between gap-2">
        <HealthPill health={overview.health} context="client" size="md" />
        {lastActivity && (
          <span
            data-testid="client-last-activity"
            title={new Date(overview.lastActivityAt as string).toLocaleString(
              'en-US',
            )}
            className="text-xs text-gray-500 dark:text-gray-400">
            {fillTemplate(t.lastActivity, { when: lastActivity })}
          </span>
        )}
      </div>

      {!isNone && (
        <div className="mt-3">
          <div className="flex items-baseline justify-between gap-2 text-sm text-gray-900 dark:text-white">
            <span data-testid="client-completion-label">
              {isComplete
                ? fillTemplate(t.allComplete, { n: overview.countableCount })
                : fillTemplate(t.tasksOfTotalComplete, {
                    complete: overview.completeCount,
                    countable: overview.countableCount,
                  })}
            </span>
            {!isComplete && (
              <span className="text-gray-500 dark:text-gray-400">
                {fillTemplate(t.percentComplete, { pct: percent })}
              </span>
            )}
          </div>
          <div
            className="mt-2"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={percent}
            aria-label={fillTemplate(t.tasksOfTotal, {
              complete: overview.completeCount,
              countable: overview.countableCount,
            })}>
            <Progress
              size="md"
              value={percent}
              variant={healthBarVariant(overview.health)}
            />
          </div>
        </div>
      )}

      {!isNone && counters.length > 0 && (
        <div
          data-testid="client-counter-tiles"
          className="mt-4 flex flex-wrap gap-2">
          {counters.map(({ kind, count }) => {
            const destination = DESTINATION[kind];
            const canNavigate =
              !!onSelectTab && visibleTabs.includes(destination.tab);
            return (
              <ClientCounter
                key={kind}
                kind={kind}
                count={count}
                variant="tile"
                onClick={
                  canNavigate
                    ? () => onSelectTab?.(destination.tab, destination.group)
                    : undefined
                }
              />
            );
          })}
        </div>
      )}

      <ClientNextUp milestones={overview.nextMilestones} />
    </Card>
  );
};

export default ClientProgressHeader;
